import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {useDisclosure} from '@mantine/hooks';
import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import type {PersonnelSimpleResponse} from 'src/api/ProactApiClient';
import {
  AttachmentModuleEnum,
  EntityIdentifierEnum,
  FeatureFlagEnum,
  ModuleEnum,
  NoteModuleEnum,
  PermissionOperationEnum,
  UserTypeEnum,
} from 'src/api/ProactApiClient';
import {CopyActionIcon} from 'src/design/molecules/CopyActionIcon';
import {CreateButton} from 'src/design/molecules/CreateButton';
import {Subtitle} from 'src/design/atoms/Typography';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {FormViewComponentV2} from 'src/app/forms/FormViewComponent/FormViewComponentV2';
import {SidebarLinkVendors} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {AttachmentsModule} from 'src/app/attachments/AttachmentsModule';
import {AuditModule} from 'src/app/audit-logs/AuditModule';
import {NotesModule} from 'src/app/notes/NotesModule';
import {VendorPersonnelTable} from 'src/pages/Modules/Vendors/VendorView/components/VendorPersonnelTable/VendorPersonnelTable';
import {
  VendorBaseFormMeta,
  VendorDocumentTitle,
  vendorInsuranceFormMeta,
} from 'src/pages/Modules/Vendors/shared/vendorHelpers';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useGetUserGroupSelectOptionsQuery} from 'src/redux/queries/userGroup';
import {
  useDeleteVendorMutation,
  useGetVendorQuery,
} from 'src/redux/queries/vendor';
import {useAuditLogPermissions} from 'src/shared/auth/permissions/useAuditLogPermissions';
import {usePersonnelPermissions} from 'src/shared/auth/permissions/usePersonnelPermissions';
import {useVendorPermissions} from 'src/shared/auth/permissions/useVendorPermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {useExtensionTabs} from 'src/shared/hooks/useExtensionTabs';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';
import {VendorActiveStatusTab} from './VendorActiveStatusTab';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

type ViewType =
  | 'overview'
  | 'personnel'
  | 'signupurl'
  | 'insurance'
  | 'notes'
  | 'attachments'
  | 'audit'
  | 'active-status'
  | '';

export const VendorViewPage = () => {
  const navigate = useAppNavigate();
  const {vendorId, view} = useParams();
  const [formData, setFormData] = useState<IFormDataV2>({});
  const {setAlert} = useAlert();
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const auditPerms = useAuditLogPermissions();
  const personnelPerms = usePersonnelPermissions();
  const vendorPerms = useVendorPermissions();
  useSetDocumentTitle(VendorDocumentTitle);

  const [formMeta, setFormMeta] = useState<IFormFieldV2[]>([]);

  const {data: usergroups, isLoading: isUserGroupsLoading} =
    useGetUserGroupSelectOptionsQuery({
      active: true,
      start: 0,
      length: 10000,
    });

  const validVendorId =
    vendorId !== undefined && !isNaN(+vendorId) ? +vendorId : undefined;

  const {data: viewItem, isLoading} = useGetVendorQuery(validVendorId!, {
    skip: !validVendorId,
  });

  const [deleteVendor] = useDeleteVendorMutation();

  const extensionTabs = useExtensionTabs(
    ModuleEnum.Vendors,
    validVendorId ?? 0
  );

  useEffect(() => {
    if (viewItem) {
      setFormData({
        ...(viewItem as IFormDataV2),
        name: viewItem?.name,
      });
    }
  }, [viewItem]);

  useEffect(() => {
    if (isLoading || isUserGroupsLoading) {
      return;
    }

    const editTaskMeta: IFormFieldV2[] = VendorBaseFormMeta(
      usergroups?.data ?? []
    );

    setFormMeta(editTaskMeta);
  }, [isLoading, isUserGroupsLoading]);

  const signupUrl = `${window.location.origin}${PathUtility.VendorsSignup(
    viewItem?.signupCode ?? ''
  )}`;

  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);

  const handlePersonnelRowClick = (row: PersonnelSimpleResponse) => {
    navigate(
      PathUtility.VendorPersonnelView(
        String(row.id),
        String(row.vendorId),
        'overview'
      )
    );
  };

  const actionBarPreNode = (
    <>
      {personnelPerms.create && (
        <CreateButton
          onClick={() =>
            navigate(PathUtility.VendorPersonnelCreate(String(vendorId)))
          }
        />
      )}
    </>
  );

  const {RenderedTabsComponent} = useUrlQueryTabV2<ViewType>({
    view: (view ?? '') as ViewType,
    defaultView: 'overview',
    tabs: [
      {
        view: 'overview',
        label: 'Overview',
        component: (
          <>
            {viewItem?.active === false && <InactiveVendorWarning />}
            <FormViewComponentV2 formData={formData} formMeta={formMeta} />
          </>
        ),
      },
      {
        view: 'active-status',
        label: 'Active Status',
        component:
          viewItem && vendorId ? (
            <VendorActiveStatusTab
              vendorId={Number(vendorId)}
              isActive={!!viewItem.active}
              onStatusChange={response =>
                setFormData(prev => ({...prev, active: response.active}))
              }
            />
          ) : null,
      },
      {
        view: 'personnel',
        label: 'Personnel',
        disabled: !personnelPerms.view,
        component: (
          <>
            {viewItem?.active === false && <InactiveVendorWarning />}
            {vendorId && (
              <VendorPersonnelTable
                identifier="vendor-personnel"
                userType={UserTypeEnum.Contractor}
                vendorId={Number(vendorId)}
                onRowClick={handlePersonnelRowClick}
                actionBarPreNode={actionBarPreNode}
              />
            )}
          </>
        ),
      },
      {
        view: 'signupurl',
        label: 'Signup URL',
        disabled: !vendorPerms.signupUrl,
        component: (
          <>
            {viewItem?.active === false && <InactiveVendorWarning />}
            {viewItem?.signupCode && (
              <Box mt="md">
                <Subtitle text="Signup Url" />
                <Text size="sm" c="dimmed" mb="sm">
                  The vendor signup code can be used to allow users to
                  self-register. To use it, share the following signup url with
                  the vendor. They can distribute it to their employees for
                  self-registration. Those users that self-register will appear
                  in the Personnel list for this vendor.
                </Text>
                <Text size="sm" fs="italic">
                  {signupUrl}
                </Text>
                <CopyActionIcon value={signupUrl} />
              </Box>
            )}
          </>
        ),
      },
      {
        view: 'insurance',
        label: 'Insurance',
        disabled: !vendorPerms.insurance,
        component: (
          <>
            {viewItem?.active === false && <InactiveVendorWarning />}
            <FormViewComponentV2
              formData={formData}
              formMeta={vendorInsuranceFormMeta}
            />
          </>
        ),
      },
      {
        view: 'notes',
        label: 'Notes',
        disabled: !vendorPerms.notes,
        component: (
          <>
            {viewItem && (
              <NotesModule
                noteType={NoteModuleEnum.Vendor}
                entityId={viewItem.id}
              />
            )}
          </>
        ),
      },
      {
        view: 'attachments',
        label: 'Attachments',
        disabled: !vendorPerms.attachments,
        component: (
          <>
            {viewItem && (
              <AttachmentsModule
                attachmentType={AttachmentModuleEnum.Vendor}
                entityId={viewItem.id}
              />
            )}
          </>
        ),
      },
      {
        view: 'audit',
        label: 'Audit',
        disabled: !auditPerms.view,
        component: viewItem?.id && (
          <AuditModule
            entityId={String(viewItem.id)}
            entityType={EntityIdentifierEnum.Vendor}
          />
        ),
      },
      ...extensionTabs,
    ],
    onSelectView: view =>
      navigate(PathUtility.VendorsView(vendorId as string, view as any), {
        replace: true,
      }),
  });

  const handleDelete = async () => {
    confirmDeleteHandlers.close();

    try {
      if (!vendorId) {
        setAlert('ID not found.', 'error');
        return;
      }

      setIsDeleting(true);

      await deleteVendor(+vendorId!).unwrap();

      setAlert(
        'Item was successfully deleted. This change may take a few minutes to appear.',
        'success'
      );

      navigate(PathUtility.Vendors('list'));
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsDeleting(false);
  };

  if (isLoading) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Vendors}>
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </ContentWrapper>
    );
  }

  if (!viewItem) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Vendors}>
        <Alert color="red" title="Not Found">
          Vendor not found.
        </Alert>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Vendors}>
      <Stack gap="lg">
        <BackBar
          fallbackTo={PathUtility.Vendors('list')}
          fallbackLabel="Back to Vendors"
          breadcrumbs={[
            {label: 'Vendors', to: PathUtility.Vendors('list')},
            {label: viewItem.name ?? 'Vendor'},
          ]}
        />

        <PageHeader>
          <PageHeader.Title
            icon={SidebarLinkVendors.icon}
            subtitle={viewItem.internalIdentifier}>
            {viewItem.name ?? 'Vendor'}
          </PageHeader.Title>

          <PageHeader.Badges>
            <PageHeader.Badge
              color={viewItem.active ? 'green' : 'yellow'}
              variant="light">
              {viewItem.active ? 'Active' : 'Inactive'}
            </PageHeader.Badge>
          </PageHeader.Badges>

          <PageHeader.Actions>
            {vendorPerms.update && (
              <PageHeader.Action
                variant="edit"
                onClick={() => navigate(PathUtility.VendorsEdit(vendorId!))}>
                Edit
              </PageHeader.Action>
            )}
            {vendorPerms.delete && (
              <PageHeader.Action
                variant="delete"
                onClick={() => confirmDeleteHandlers.open()}
                loading={isDeleting}>
                Delete
              </PageHeader.Action>
            )}
          </PageHeader.Actions>
        </PageHeader>

        {RenderedTabsComponent}
      </Stack>

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        title="Are you sure you want to delete this vendor?"
        body="This action cannot be undone. All users belong to this vendor will also be deleted, including their user data."
      />
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.VendorsView(':vendorId', ':view'),
  element: <VendorViewPage />,
  module: ModuleEnum.Vendors,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});

export default VendorViewPage;

const InactiveVendorWarning: React.FC = () => {
  return (
    <Alert title="Inactive Vendor" color="yellow" mb="md">
      This vendor is currently inactive. Signup URLs and vendor contractors are
      disabled. To re-enable Signup URLs for this vendor, reactivate the vendor.
      Inactive users are not automatically reactivated.
    </Alert>
  );
};
